/**
 * The drawing set as DXF, written by hand.
 *
 * Every kitchen maker's software opens DXF, and none of them has our importer -- the same
 * reasoning that made the cabinet schedule a CSV (schedule.js) and the report a hand-drawn
 * PDF (report.js). The entity set a kitchen drawing needs is LINE, POLYLINE, TEXT, ARC and
 * CIRCLE, plus a LAYER table. That is about a hundred lines of a format that has not changed
 * since 1992, against a dependency and its dependencies.
 *
 * R12 (AC1009) on purpose. It is the most universally readable DXF there is: LibreCAD,
 * Fusion, Rhino, AutoCAD, every CAM package a cabinet shop owns, and Inkscape. Later
 * versions buy features this drawing does not use and lose readers that matter.
 *
 * TWO THINGS THAT GO WRONG, both encoded here rather than discovered later:
 *
 *   THE Y AXIS. DXF is y-up; this app is y-down (see room.js). So y is negated exactly once,
 *   at this boundary, the same flip scene.js does for the floor shape. Twice and it is back
 *   to front; nowhere and the drawing is mirrored.
 *
 *   GROUP CODES COME IN PAIRS. Every value is a code line then a value line, and one stray
 *   line shifts every pair after it -- which a reader reports as a corrupt file with no clue
 *   where. `pair()` is the only way anything is written here, and the test counts them.
 *
 * MODEL UNITS, 1 unit = 1 mm, everything in modelspace. A maker opening the DXF wants to
 * measure real millimetres, not paper ones -- so text, whose size is in PAPER millimetres,
 * is divided by the sheet's scale on the way in. Sheets are laid out side by side with a gap.
 */
import { LAYER, toPaper } from './drawing.js'

/** ACI colour numbers. DXF is indexed-colour; these are the nearest to the layer palette. */
const ACI = { wall: 7, cabinet: 8, bench: 7, fitting: 9, dim: 1, text: 7, service: 30, frame: 7, faint: 8 }

const pair = (code, value) => `${code}\n${value}\n`
const n = (v) => (Math.round(v * 1000) / 1000).toString()

/**
 * @param {Array} sheets  from drawing.sheets()
 * @param {{ gap?: number }} opts  space between sheets in the modelspace layout, in mm
 * @returns {string} a complete DXF file
 */
export function toDxf(sheets, opts = {}) {
  const gap = opts.gap ?? 500
  let out = ''

  // ── HEADER ────────────────────────────────────────────────────────────────────────
  out += pair(0, 'SECTION') + pair(2, 'HEADER')
  out += pair(9, '$ACADVER') + pair(1, 'AC1009')
  out += pair(9, '$INSUNITS') + pair(70, 4)          // 4 = millimetres
  out += pair(9, '$EXTMIN') + pair(10, 0) + pair(20, 0)
  out += pair(9, '$EXTMAX') + pair(10, 10000) + pair(20, 10000)
  out += pair(0, 'ENDSEC')

  // ── TABLES: the layers, so a reader can turn the dimensions off ───────────────────
  const used = [...new Set(sheets.flatMap((s) => s.prims.map((p) => p.layer ?? 'cabinet')))]
    .filter((l) => LAYER[l])
  out += pair(0, 'SECTION') + pair(2, 'TABLES')
  out += pair(0, 'TABLE') + pair(2, 'LAYER') + pair(70, used.length)
  for (const l of used) {
    out += pair(0, 'LAYER') + pair(2, l.toUpperCase()) + pair(70, 0)
      + pair(62, ACI[l] ?? 7) + pair(6, 'CONTINUOUS')
  }
  out += pair(0, 'ENDTAB') + pair(0, 'ENDSEC')

  // ── ENTITIES ──────────────────────────────────────────────────────────────────────
  out += pair(0, 'SECTION') + pair(2, 'ENTITIES')
  let ox = 0
  for (const sheet of sheets) {
    out += entities(sheet, ox)
    ox += sheet.size.w * sheet.scale + gap * sheet.scale
  }
  out += pair(0, 'ENDSEC')
  out += pair(0, 'EOF')
  return out
}

function entities(sheet, ox) {
  let out = ''
  const layer = (p) => (p.layer ?? 'cabinet').toUpperCase()

  /*
   * Every point, in model millimetres, with the sheet's own offset applied and y flipped.
   *
   * A `paper: true` primitive -- the title block, the schedule table -- is already in paper
   * millimetres, so it is multiplied UP by the scale to land in the same modelspace as the
   * geometry. Otherwise a title block would be a millimetre tall next to a four-metre wall.
   */
  const P = (pt, paper) => {
    const q = paper
      ? { x: pt.x * sheet.scale, y: pt.y * sheet.scale }
      : (() => {
        // toPaper already turns an elevation the right way up; see drawing.js.
        const pp = toPaper(sheet, pt)
        return { x: pp.x * sheet.scale, y: pp.y * sheet.scale }
      })()
    // The one y flip: DXF counts up the page, this app counts down it.
    return { x: q.x + ox, y: -q.y }
  }

  for (const p of sheet.prims) {
    const L = layer(p)
    if (p.k === 'line') {
      const a = P(p.a, p.paper), b = P(p.b, p.paper)
      out += pair(0, 'LINE') + pair(8, L)
        + pair(10, n(a.x)) + pair(20, n(a.y)) + pair(30, 0)
        + pair(11, n(b.x)) + pair(21, n(b.y)) + pair(31, 0)
    } else if (p.k === 'poly') {
      // A POLYLINE is a header, a VERTEX per point, and a SEQEND. R12 has no LWPOLYLINE.
      out += pair(0, 'POLYLINE') + pair(8, L) + pair(66, 1) + pair(70, p.close ? 1 : 0)
        + pair(10, 0) + pair(20, 0) + pair(30, 0)
      for (const pt of p.pts) {
        const q = P(pt, p.paper)
        out += pair(0, 'VERTEX') + pair(8, L) + pair(10, n(q.x)) + pair(20, n(q.y)) + pair(30, 0)
      }
      out += pair(0, 'SEQEND') + pair(8, L)
    } else if (p.k === 'circle') {
      const c = P(p.c, p.paper)
      out += pair(0, 'CIRCLE') + pair(8, L)
        + pair(10, n(c.x)) + pair(20, n(c.y)) + pair(30, 0)
        + pair(40, n(p.paper ? p.r * sheet.scale : p.r))
    } else if (p.k === 'arc') {
      const c = P(p.c, p.paper)
      // DXF angles are degrees counter-clockwise. The y flip reverses their sense, so the
      // start and end swap: an arc drawn from A to B on screen runs from B to A here.
      const deg = (rad) => ((-rad * 180 / Math.PI) % 360 + 360) % 360
      out += pair(0, 'ARC') + pair(8, L)
        + pair(10, n(c.x)) + pair(20, n(c.y)) + pair(30, 0)
        + pair(40, n(p.paper ? p.r * sheet.scale : p.r))
        + pair(50, n(deg(p.to))) + pair(51, n(deg(p.from)))
    } else if (p.k === 'text') {
      const q = P(p.at, p.paper)
      // Size is PAPER millimetres, so it is multiplied into model units by the scale --
      // which is what keeps a 2.4mm label 2.4mm on paper at 1:50 and at 1:20 alike.
      const height = p.size * sheet.scale
      const just = p.align === 'centre' ? 1 : p.align === 'right' ? 2 : 0
      out += pair(0, 'TEXT') + pair(8, L)
        + pair(10, n(q.x)) + pair(20, n(q.y)) + pair(30, 0)
        + pair(40, n(height))
        + pair(1, String(p.text ?? '').replace(/\n/g, ' '))
        + pair(50, n(-(p.rot ?? 0)))
        + pair(72, just)
      // A justified TEXT needs its alignment point in codes 11/21 as well; readers that
      // ignore 72 without it silently left-align everything.
      if (just) out += pair(11, n(q.x)) + pair(21, n(q.y)) + pair(31, 0)
    }
  }
  return out
}
